import { useCallback, useState } from "react";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { clientService } from "@/features/clients/services/client.service";
import { CNPJ_LENGTH, CPF_LENGTH, cleanDocument } from "@/shared/lib/document";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";

const LOOKUP_DEBOUNCE_MS = 400;

export interface BuyerClientLookup {
  client: ClientResponseDTO | null;
  searching: boolean;
  notFound: boolean;
}

function isCompleteDocument(document: string): boolean {
  return document.length === CPF_LENGTH || document.length === CNPJ_LENGTH;
}

export function useBuyerClientLookup(document: string): BuyerClientLookup {
  const [client, setClient] = useState<ClientResponseDTO | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [searching, setSearching] = useState(false);

  const normalizedDocument = cleanDocument(document);
  const isComplete = isCompleteDocument(normalizedDocument);

  const fetcher = useCallback(
    async (term: string, signal: AbortSignal): Promise<ClientResponseDTO[]> => {
      const response = await clientService.search(
        { document: term, size: 5 },
        { signal },
      );
      return response.content;
    },
    [],
  );

  const setItems = useCallback(
    (items: ClientResponseDTO[]) => {
      if (!isComplete) {
        setClient(null);
        setNotFound(false);
        return;
      }
      const match =
        items.find(
          (candidate) =>
            cleanDocument(candidate.document) === normalizedDocument,
        ) ?? null;
      setClient(match);
      setNotFound(match === null);
    },
    [normalizedDocument, isComplete],
  );

  const handleError = useCallback(() => {
    setClient(null);
    setNotFound(false);
  }, []);

  useDebouncedSearch<ClientResponseDTO>({
    term: normalizedDocument,
    enabled: isComplete,
    isValid: isCompleteDocument,
    debounceMs: LOOKUP_DEBOUNCE_MS,
    fetcher,
    setItems,
    setLoading: setSearching,
    onError: handleError,
  });

  return { client, searching, notFound };
}
